<template>
  <div class="contact-container">
    <h1>Contact Us</h1>
    <p>Have questions? Reach out to us, and we’ll get back to you as soon as possible.</p>

    <form @submit.prevent="submitForm">
      <div class="form-group">
        <label for="name">Name</label>
        <input type="text" id="name" v-model="formData.name" required />
      </div>

      <div class="form-group">
        <label for="email">Email</label>
        <input type="email" id="email" v-model="formData.email" required />
      </div>

      <div class="form-group">
        <label>Services Interested In</label>
        <div class="pills-container">
          <span
              v-for="service in services"
              :key="service.name"
              class="service-pill"
              @click="toggleSelection(service.name)"
              :class="{ 'selected': selectedServices.includes(service.name) }"
          >
            {{ service.name }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <label for="industry">Industry (Optional)</label>
        <input type="text" id="industry" v-model="formData.industry" />
      </div>

      <button type="submit" :disabled="loading">
        {{ loading ? "Submitting..." : "Send Message" }}
      </button>
    </form>

    <p v-if="successMessage" class="success-message">{{ successMessage }}</p>
    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const formData = ref({
  name: '',
  email: '',
  industry: ''
});

const services = [
  { name: "AI / ML" },
  { name: "Data" },
  { name: "ETL / ELT" },
  { name: "API-Led Architectures" },
  { name: "Microservices" },
  { name: "Enterprise Architecture" },
  { name: "Other" }
];

const selectedServices = ref([]);
const loading = ref(false);
const successMessage = ref("");
const errorMessage = ref("");

const toggleSelection = (service) => {
  const index = selectedServices.value.indexOf(service);
  if (index > -1) {
    selectedServices.value.splice(index, 1);
  } else {
    selectedServices.value.push(service);
  }
};

const submitForm = async () => {
  loading.value = true;
  successMessage.value = "";
  errorMessage.value = "";

  const formPayload = {
    name: formData.value.name,
    email: formData.value.email,
    services: selectedServices.value,
    industry: formData.value.industry || "",
  };

  try {
    const response = await fetch(
        process.env.NODE_ENV === "development"
            ? "http://localhost:3001/submit"
            : "/api/submit", // ✅ This works in Vercel
        {
          method: "POST",
          body: JSON.stringify(formPayload),
          headers: { "Content-Type": "application/json" },
        }
    );

    // const response = await fetch('/submit', { // 🔥 Uses proxy now
    //   method: "POST",
    //   body: JSON.stringify(formPayload),
    //   headers: { "Content-Type": "application/json" },
    // });


    const result = await response.json();
    if (!result.success) throw new Error(result.error);

    successMessage.value = "Your message has been sent!";
    formData.value.name = "";
    formData.value.email = "";
    formData.value.industry = "";
    selectedServices.value = [];
  } catch (error) {
    console.error("Error submitting form:", error);
    errorMessage.value = "Failed to submit form.";
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.contact-container {
  max-width: 600px;
  margin: auto;
  padding: 20px;
  text-align: center;
}
h1 {
  font-size: 2rem;
  margin-bottom: 10px;
}
.form-group {
  margin-bottom: 15px;
  text-align: left;
}
label {
  display: block;
  font-weight: bold;
}
input {
  width: 100%;
  padding: 10px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

/* Pills Styling */
.pills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.service-pill {
  padding: 10px 15px;
  border-radius: 20px;
  border: 2px solid #ff9800;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  color: #d3d3d3;
}

.service-pill.selected {
  background: linear-gradient(135deg, #ff9800, #ff5722);
  color: white;
  border: none;
}

button {
  background: linear-gradient(135deg, #ff9800, #ff5722);
  color: white;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  margin-top: 10px;
}

.success-message {
  color: green;
  font-weight: bold;
  margin-top: 10px;
}

.error-message {
  color: red;
  font-weight: bold;
  margin-top: 10px;
}
</style>
